"use client";

import { useSpring, animated } from "@react-spring/web";
import Image from "next/image";
import clap from "@/images/svgs/clap.svg";
import { useAtom } from "jotai";
import { clientIdAtom } from "@/context/HydrateClientIdAtom";
import toast from "react-hot-toast";
import clsx from "clsx";
import { ReactionType } from "@prisma/client";
import { trpc } from "@/utils/trpc/hooks";
import { useCallback, useState } from "react";
import { useSession } from "next-auth/react";
import { getQueryKey } from "@trpc/react-query";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { ShareLinks } from "@/components/ShareLinks";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import BookmarkOutlineIcon from "@/components/icons/BookmarkOutlineIcon";
import ShareOutlineIcon from "@/components/icons/ShareOutlineIcon";

interface BlogPostReactionsProps {
  blogPostId: string;
  blogPostSlug: string;
}

type Reaction = "CLAP";

type ReactionMap = {
  icon: string;
  payload: ReactionType;
  count: number;
  initialReactionKey: Reaction;
};

function mapReaction(reaction: Reaction, count: number): ReactionMap {
  const icon = {
    CLAP: clap,
  }[reaction];

  const payload = {
    CLAP: ReactionType.CLAP,
  }[reaction];

  return {
    icon,
    payload,
    count: count ?? 0,
    initialReactionKey: reaction,
  };
}

function transformReactionsToMap(reactions) {
  if (!reactions || !Array.isArray(reactions)) {
    return {};
  }

  return reactions.reduce((acc, curr) => {
    acc[curr.type] = curr.count;
    return acc;
  }, {});
}

export function BlogPostReactions({
  blogPostId,
  blogPostSlug,
}: BlogPostReactionsProps) {
  const [clientId] = useAtom(clientIdAtom);
  const props = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 1000,
  });
  const [reactionDiff, setReactionDiff] = useState({
    CLAP: 0,
  });
  const session = useSession();
  const queryClient = useQueryClient();

  const findManyBlogPostReactionQueryArgs = () => {
    const where =
      session.status === "authenticated"
        ? {
            blogPostId: blogPostId,
            userId: session.data?.user?.id,
          }
        : {
            blogPostId: blogPostId,
            clientId,
          };

    return {
      where,
      select: {
        type: true,
        clientId: true,
        userId: true,
      },
    };
  };

  const { data: reactionsData } =
    trpc.blogpostreaction.findManyBlogPostReaction.useQuery(
      findManyBlogPostReactionQueryArgs(),
      {
        enabled: session.status !== "loading" && clientId !== null,
        // Only refetch when the page mounts
        staleTime: Infinity,
        refetchOnMount: "always",
      }
    );

  const { data: savedBlogPost } =
    trpc.usersavedblogpost.findUniqueUserSavedBlogPost.useQuery(
      {
        where: {
          user_id_blog_post_id: {
            blogPostId,
            userId: session.data?.user?.id ?? "",
          },
        },
      },
      {
        enabled: session.status === "authenticated",
        // Only refetch when the page mounts
        staleTime: Infinity,
        refetchOnMount: "always",
      }
    );

  const reactionsDataQueryKey = getQueryKey(
    trpc.blogpostreaction.findManyBlogPostReaction,
    findManyBlogPostReactionQueryArgs(),
    "query"
  );

  const savedBlogPostDataQueryKey = getQueryKey(
    trpc.usersavedblogpost.findUniqueUserSavedBlogPost,
    {
      where: {
        user_id_blog_post_id: {
          blogPostId,
          userId: session.data?.user?.id ?? "",
        },
      },
    },
    "query"
  );

  const { data: reactionsArr, isFetching } = useQuery({
    initialData: {
      reactions: [
        {
          type: ReactionType.CLAP,
          count: 0,
        },
      ],
    },
    queryKey: ["BlogPostReactions", blogPostId],
    queryFn: () =>
      fetch(`/api/v1/blogPostReactions?id=${blogPostId}`).then((res) =>
        res.json()
      ),
    staleTime: Infinity,
    refetchOnMount: "always",
  });

  const finalReactions = transformReactionsToMap(reactionsArr.reactions || []);

  const { mutateAsync: addReaction } =
    trpc.blogpostreaction.createOneBlogPostReaction.useMutation();
  const { mutateAsync: removeReaction } =
    trpc.blogpostreaction.deleteOneBlogPostReaction.useMutation();
  const { mutateAsync: addSavedPost } =
    trpc.usersavedblogpost.createOneUserSavedBlogPost.useMutation();
  const { mutateAsync: removeSavedPost } =
    trpc.usersavedblogpost.deleteOneUserSavedBlogPost.useMutation();

  const handleClick = useCallback(
    async (reaction: Reaction) => {
      const payload = session.data?.user
        ? {
            userId: session.data.user.id,
          }
        : {
            clientId,
          };

      setReactionDiff((prev) => ({
        ...prev,
        [reaction]: prev[reaction] + 1,
      }));

      await addReaction(
        {
          data: {
            ...payload,
            blogPostId,
            type: reaction,
          },
        },
        {
          onSuccess: () => {
            queryClient.setQueryData(reactionsDataQueryKey, [
              ...(reactionsData || []),
              {
                type: reaction,
              },
            ]);
          },
        }
      );
    },
    [
      addReaction,
      blogPostId,
      queryClient,
      reactionsData,
      reactionsDataQueryKey,
      session,
      setReactionDiff,
      clientId,
    ]
  );

  const handleRemoveReaction = useCallback(
    async (reaction: Reaction) => {
      if (!session.data?.user && !clientId) {
        toast.error("You must be logged in to react.");
        return;
      }

      const payload = session.data?.user
        ? {
            user_id_blog_post_id_type: {
              userId: session.data.user.id,
              blogPostId: blogPostId,
              type: reaction,
            },
          }
        : {
            client_id_blog_post_id_type: {
              clientId: clientId as string,
              blogPostId: blogPostId,
              type: reaction,
            },
          };

      setReactionDiff((prev) => ({
        ...prev,
        [reaction]: prev[reaction] - 1,
      }));
      await removeReaction(
        {
          where: payload,
        },
        {
          onSuccess: () => {
            queryClient.setQueryData(
              reactionsDataQueryKey,
              (reactionsData || []).filter((r) => r.type !== reaction)
            );
          },
        }
      );
    },
    [
      blogPostId,
      queryClient,
      reactionsData,
      reactionsDataQueryKey,
      removeReaction,
      session,
      setReactionDiff,
      clientId,
    ]
  );

  const handleSavePost = useCallback(async () => {
    if (!session.data?.user) {
      toast.error("You must be logged in to save a post.");
      return;
    }

    await addSavedPost(
      {
        data: {
          userId: session.data.user.id,
          blogPostId,
        },
      },
      {
        onSuccess: () => {
          queryClient.setQueryData(savedBlogPostDataQueryKey, {
            userId: session.data.user.id,
            blogPostId,
          });
        },
      }
    );
  }, [
    addSavedPost,
    blogPostId,
    queryClient,
    savedBlogPostDataQueryKey,
    session,
  ]);

  const handleRemoveSavedPost = useCallback(async () => {
    if (!session.data?.user) {
      toast.error("You must be logged in to save a post.");
      return;
    }

    await removeSavedPost(
      {
        where: {
          user_id_blog_post_id: {
            userId: session.data.user.id,
            blogPostId,
          },
        },
      },
      {
        onSuccess: () => {
          queryClient.setQueryData(savedBlogPostDataQueryKey, null);
        },
      }
    );
  }, [
    blogPostId,
    queryClient,
    removeSavedPost,
    savedBlogPostDataQueryKey,
    session,
  ]);

  const handleOnClick = useCallback(
    (data: ReactionMap) => {
      if (reactionsData?.some((d) => d.type === data.payload)) {
        handleRemoveReaction(data.payload);
      } else {
        handleClick(data.payload);
      }
    },
    [reactionsData, handleRemoveReaction, handleClick]
  );

  // async function addReaction(data) {
  //   "use server";
  //   const session = await getServerSession(authOptions);

  //   if (!session) {
  //     return;
  //   }

  //   await prisma?.blogPostReaction.create({
  //     data: {
  //       userId: session.user.id,
  //       blogPostId: blogPostId,
  //       type: data.type,
  //     },
  //   });
  // }

  // TODO: Implement
  // async function removeReaction(data) {
  //   "use server";
  //   const session = await getServerSession(authOptions);

  //   if (!session) {
  //     return;
  //   }

  //   await prisma?.blogPostReaction.delete({
  //     where: {
  //       user_id_blog_post_id_type: {
  //         userId: session.user.id,
  //         blogPostId: blogPostId,
  //         type: data.type,
  //       },
  //     },
  //   });
  // }

  if (!finalReactions) {
    return null;
  }

  const mappedReactions = {
    clap: mapReaction("CLAP", finalReactions.CLAP),
  };

  return (
    <animated.div style={props} className="fixed left-0 right-0 bottom-4 z-50">
      <div className="mx-auto flex items-center justify-center">
        <div className="flex border bg-white border-gray-100 gap-2 items-center divide-x justify-center shadow-lg p-2 px-4 rounded-full">
          {Object.entries(mappedReactions).map(([reaction, data]) => {
            return (
              <div key={reaction}>
                <button
                  disabled={isFetching}
                  className={clsx(
                    "ml-2 p-2 hover:bg-gray-100 rounded-md animate-colors",
                    {
                      "bg-blue-50 hover:bg-blue-200":
                        reactionDiff[data.initialReactionKey] === 1 ||
                        reactionsData?.some((d) => d.type === data.payload),
                      "hover:bg-white opacity-50": isFetching,
                    }
                  )}
                  onClick={() => handleOnClick(data)}
                >
                  <span className="flex justify-center items-center gap-2">
                    <Image
                      src={data.icon}
                      width={24}
                      height={24}
                      alt="Clap reaction"
                    />
                    {!isFetching && (
                      <span>
                        {`${
                          Number(data.count) +
                          reactionDiff[data.initialReactionKey]
                        }`}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            );
          })}
          <div>
            <button
              disabled={isFetching}
              className={clsx(
                "ml-2 p-2 hover:bg-gray-100 rounded-md animate-colors",
                {
                  "hover:bg-white opacity-50": isFetching,
                }
              )}
              onClick={() => {
                if (savedBlogPost) {
                  handleRemoveSavedPost();
                } else {
                  handleSavePost();
                }
              }}
            >
              <span className="flex justify-center items-center gap-2">
                <BookmarkOutlineIcon
                  className={clsx("w-6 h-6 text-black", {
                    "text-yellow-400 fill-yellow-400": !!savedBlogPost,
                  })}
                />
              </span>
            </button>
          </div>
          <div className="flex items-center justify-center px-2">
            <Popover>
              <PopoverTrigger className="hover:bg-gray-100 p-2 rounded-md">
                <ShareOutlineIcon className="w-6 h-6" />
              </PopoverTrigger>
              <PopoverContent side="top" className="mb-4 p-2 max-w-[164px]">
                <ShareLinks
                  shareText="melbcoffee.com shared some cheeky goodness"
                  shareLink={`https://melbcoffee.com/posts/${blogPostSlug}`}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </animated.div>
  );
}
